import { List, useStyles } from '@archipro-design/aria';
import * as S from './MobileBottomMenuContent.style';
import {
    ArrowRightSLine,
    CustomCopy,
    CustomTrash,
    ExternalLinkLine,
    FolderTransferLine,
    InboxCustom,
    AddLine,
} from '@archipro-design/icons';
import { useDesignBoardPageState } from '~/modules/design-board/component/context';
import {
    getPinProfessional,
    getPinWebsiteLink,
} from '~/modules/design-board/util';
import { useDeletePin } from '~/modules/design-board/hook/use-delete-pin';
import { DesignBoardPinTargetType } from 'generated/graphql';
import {
    SHOW_COMMENT,
    SHOW_SHARE_PINS,
} from '../../config/design-board-config';
import LoginSignUpModal from '@modules/user/component/login-signup-modal/LoginSignUpModal';
import { isUserLoggedIn, useUser } from '@modules/user';
import { useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';

export const MobilePinMenu = () => {
    const { css } = useStyles();
    const {
        dispatch,
        state: { currentPinData },
    } = useDesignBoardPageState();

    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const [openLoginModal, setOpenLoginModal] = useState(false);

    const { deletePin, deletePinDialog } = useDeletePin();

    const tracker = useTracker();

    const isUploadFile =
        currentPinData?.TargetFilterType ===
        DesignBoardPinTargetType.MemberUpload;

    const pinWebsiteLink =
        currentPinData && !isUploadFile
            ? getPinWebsiteLink(currentPinData)
            : '';

    const closeBottomMenu = () => {
        dispatch({ type: 'CloseDrawer' });
    };

    const pinProfessional = currentPinData
        ? getPinProfessional(currentPinData)
        : undefined;

    const {
        referralTrackingURL,
        referralTrackingClass,
        referralTrackingParams,
    } = useWebsiteReferralTracking(pinWebsiteLink, {
        libraryItemID: currentPinData?.Target.ID || 0,
        professionalTitle: pinProfessional?.Title || '',
    });

    const onClickVisitWebsite = () => {
        if (currentPinData) {
            tracker.log('PinMenuVisitWebsite', {
                label: pinProfessional?.Title || '',
                url: new URL(window.location.href),

                data: {
                    LibraryItemID: currentPinData.Target.ID,
                    ProfessionalID: pinProfessional?.ID || 0,
                },
            });
        } else {
            console.error('No pin data found');
        }

        closeBottomMenu();
    };

    const onClickShareLink = () => {
        const shareData = {
            title: currentPinData?.Title,
            url: currentPinData?.Link,
        };

        closeBottomMenu();

        if (shareData.url && navigator.share) {
            navigator.share(shareData);
        }
    };

    const onClickMove = () => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else if (currentPinData !== undefined) {
            dispatch({
                type: 'OpenMovePinsDrawer',
                pinMoveData: {
                    boardId: currentPinData?.DesignBoard.ID,
                    pinListString: currentPinData.ID.toString(),
                },
            });
        }
    };

    const onClickCopy = () => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else if (currentPinData !== undefined) {
            dispatch({
                type: 'OpenCopyPinsDrawer',
                pinMoveData: {
                    boardId: currentPinData?.DesignBoard.ID,
                    pinListString: currentPinData.ID.toString(),
                },
            });
        }
    };

    const onClickComments = () => {
        //Reimplement comments when they are created
        //https://archipro.atlassian.net/browse/PLAT-4011
        console.log(
            '🚀 ~ file: MobileBoardMenu.tsx:63 ~ onClickComments ~ onClickComments:'
        );
        closeBottomMenu();
    };

    const onClickDelete = () => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else {
            tracker.log('PinMenuDelete', {
                label: currentPinData?.TileData?.professionalTitle || '',
                url: new URL(window.location.href),
                data: {
                    ItemType: 'pin',
                    ItemID: currentPinData?.ID || 0,
                },
            });
            deletePin(currentPinData?.ID || 0);
        }
    };

    const menuItems = [
        !!pinWebsiteLink && {
            label: 'Visit company website',
            link: referralTrackingURL,
            linkClassName: referralTrackingClass,
            linkAttributes: referralTrackingParams,
            openNewWindow: true,
            onClick: onClickVisitWebsite,
            iconBefore: <ExternalLinkLine className="visit-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
        },
        {
            label: 'Move',
            onClick: onClickMove,
            iconBefore: <FolderTransferLine className="move-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
        },
        {
            label: 'Copy',
            onClick: onClickCopy,
            iconBefore: <CustomCopy className="copy-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
        },
        //Reimplement comments when they are created
        //https://archipro.atlassian.net/browse/PLAT-4011
        SHOW_COMMENT && {
            label: 'Comments',
            onClick: onClickComments,
            iconBefore: <InboxCustom className="comment-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
        },
        SHOW_SHARE_PINS &&
            currentPinData?.Link &&
            !isUploadFile && {
                label: 'Share',
                onClick: onClickShareLink,
                iconBefore: <AddLine className="share-icon" />,
                iconAfter: <ArrowRightSLine className="arrow-icon" />,
            },
        {
            label: 'Delete',
            onClick: onClickDelete,
            iconBefore: <CustomTrash className="del-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
        },
    ];

    return (
        <>
            <div className={css(S.BottomMenuWrapper)}>
                <List items={menuItems} />
                {deletePinDialog(css(S.DeleteDialog))}
            </div>
            <LoginSignUpModal
                login={{
                    onCancel: () => setOpenLoginModal(false),
                    onAuthSuccess: () => setOpenLoginModal(false),
                    open: openLoginModal,
                    flow: null,
                    authSource: 'designBoard',
                }}
                forgotPassword={{}}
            />
        </>
    );
};
